
import { Component, HostListener, Inject, Input, OnInit, ViewEncapsulation, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Align } from '@progress/kendo-angular-popup';
import { forkJoin } from 'rxjs';
import { Option } from '~/front-models/option';
import { UserSummary } from '~/models/administration/user';
// import { Site } from '~/models/site';
import { ReportViewerPopupComponent } from '~/shared/components/viewer/popup.component';
import { ReportsService } from '~/services/modules/reports/reports';
import { FavoritesService } from '~/services/modules/stocks/favorites';
import { SecurityService } from '~/services/security.service';
import { ThemeManagerService } from '~/services/theme-manager';
import { PageState } from '~/shared/enums/stock-files.enum';
import { SitesEnum } from '~/shared/enums/sites.enum';
import { LayoutManagerService } from '../layout-manager';
import { LayoutComponent } from '../layout.component';
import { EnvironmentLoaderService } from '~/services/BackEndURL/environment-loader.service';
import { AccountsService } from '~/services/administration.ts/accounts';
import { AccountSite } from '~/models/AccountSite';
import { MultiSiteService } from '~/services/modules/common/multi-site.service';
import { SharedService } from '~/services/sharedService';
import { PageNames } from '~/models/pageNames';
import { bellIcon, menuIcon, SVGIcon } from "@progress/kendo-svg-icons";

@Component({
    selector: 'asap-layout-navbar',
    templateUrl: './navbar.html',
    styleUrls: ['./navbar.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'asap-layout-navbar',
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class Navbar implements OnInit {
  _themeToggleChecked: boolean = false;
  isAdmin: boolean;
  _userFullName: string;
  _email: string;
  _semiUserName: string;
  _userProfilePic: string;
  _haveShopperLicense?: boolean = false;
  theme: string;
  isExpanded = true;
  allowCustom: boolean = true;
  favoritePages: Array<any> = [];
  _currentUser: UserSummary;
  _isEnterprise: boolean;
  _isShopping: boolean;
  _inAssetBase: boolean = false;
  _inStockBase: boolean = false;
  _isAsset: boolean = false;
  _isStock: boolean = false;
  public zoomlist: Array<string> = ['80%', '90%', '100%', '110%'];
  _selectedZoom: string;
  _anchorAlign: Align = { horizontal: 'left', vertical: 'top' }

  selectedSite: any;
  selectedAccount: AccountSite;
  _userSites: any[];
  _items: AccountSite[];
  _currentSiteName: string;
  _loggedInAccountSites: string = "";
  _isMultiSite: boolean = false;

  public menuIcon: SVGIcon = menuIcon;
  public bellIcon: SVGIcon = bellIcon;
  public kendokaAvatar = "assets/navigation/appbar/kendoka-angular.png";

  @Input() options: Array<Option>;
  @Input() base: string;

  // public showNavPopup = false;
  // public showSitePopup = false;
  // public showHelpPopup = false;
  // public showAdminPopup = false;
  // public showFavPopup = false;

  //public margin = { horizontal: -46, vertical: 7 };


  constructor(private _securityService: SecurityService,
    private _themeManager: ThemeManagerService,
    private _favoritesService: FavoritesService,
    private _layoutComponent: LayoutComponent,
    private _layoutManagerService: LayoutManagerService,
    private router: Router,
    private _reportService: ReportsService,
    private _accountsites: AccountsService,
    private _sitesService: MultiSiteService,
    public _sharedService: SharedService,
    @Inject(DOCUMENT) private document: Document) {
    this._themeManager.themeChange.subscribe((data) => {
      this.theme = data;

      if (this.theme == 'dark') {
        this._themeToggleChecked = true;
      }
      else {
        this._themeToggleChecked = false;
      }
    });

    this._getFavoritePages();

    this._layoutManagerService.favoritesChange.subscribe((x) => {
      if (x) {
        this.favoritePages = [];
        let favoritePagesGroups = new Set(x.map((item: { category: any }) => item.category));

        favoritePagesGroups.forEach(g =>
          this.favoritePages.push({ category: g, values: x.filter((i: { category: unknown }) => i.category === g) }));
      }
    });

    this._currentUser = this._securityService.currentUser;

    if (this._currentUser.appRole.name != undefined) {
      this.isAdmin = this._currentUser.appRole.name === "Admin";
    }
  }

  ngOnInit() {
    if (this._currentUser) {
      this.getLicense();

      this._userFullName = `${this._currentUser.firstName} ${this._currentUser.lastName}`;
      this._email = this._currentUser.userName;
      this._semiUserName = this._currentUser.userName.split('@')[0];
      this._isEnterprise = this._currentUser.isEnterprise;

      if (this._currentUser.person?.profileImage?.thumbnail) {
        const thumb = this._currentUser.person.profileImage.thumbnail.replace(/\\/g, '/');
        const baseUrl = (EnvironmentLoaderService.envConfig?.enterpriseApiLocal || '').replace(/\/+$/, '');
        this._userProfilePic = baseUrl ? `${baseUrl}/${thumb}` : thumb;
      }

      if (!this._isEnterprise) {
        this._checkSecurity();
      }
    }
  }

  getLicense() {
    forkJoin({
      licenses: this._securityService.getLicense(),
      baseLicense: this._securityService.getBaseLicense()
    }).subscribe(({ licenses, baseLicense }: any) => {
      this._isShopping = this._currentUser.person?.isShopping && licenses?.general?.shoppingCart;
      this._inAssetBase = !!licenses?.asset?.inAssetBase;
      this._inStockBase = !!licenses?.stock?.instockBase;
      this._isAsset = !!baseLicense?.isAsset;
      this._isStock = !!baseLicense?.isStock;
    });
  }


  activePopup: string | null = null;

  get helpUrls() {
    const config = EnvironmentLoaderService.envConfig;
    return {
      wikiUrl: config?.wikiUrl || 'https://www.asapsystems.com/wiki/barcloud/',
      contactSupportUrl: config?.contactSupportUrl || config?.supportUrl || 'https://www.asapsystems.com/inventory-asset-tracking-support/',
      scheduleTrainingUrl: config?.scheduleTrainingUrl || (config as any)?.trainingUrl || 'https://outlook.office365.com/owa/calendar/ASAPSystemsTrainingandSupport@asapsystems.com/bookings/',
    };
  }

  togglePopup(popupName: string): void {
    this.activePopup = this.activePopup === popupName ? null : popupName;
    this._getFavoritePages();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const clickedElement = event.target as HTMLElement;

    // List all elements that can toggle popups
    if (
      clickedElement.closest('.overflow-button') || // Main menu button
      clickedElement.closest('.nav-link') || // Favorites, Admin, Help, etc.
      clickedElement.closest('kendo-popup') // Popups themselves
    ) {
      return; // Do nothing if click is inside a popup or button
    }

    // Otherwise, close all popups
    this.activePopup = null;
  }

  // public toggleNavPopup(): void {
  //   //this.hideAllPopups();
  //   this.showNavPopup = !this.showNavPopup;

  //   this.showSitePopup = false;
  //   this.showHelpPopup = false;
  //   this.showAdminPopup = false;
  //   this.showFavPopup = false;
  // }

  // public toggleSitePopup(): void {
  //   this.showSitePopup = !this.showSitePopup;

  //   this.showNavPopup = false;
  //   this.showHelpPopup = false;
  //   this.showAdminPopup = false;
  //   this.showFavPopup = false;
  // }

  // public toggleHelpPopup(): void {

  //   this.showHelpPopup = !this.showHelpPopup;

  //   this.showNavPopup = false;
  //   this.showSitePopup = false;
  //   this.showAdminPopup = false;
  //   this.showFavPopup = false;
  // }

  // public toggleAdminPopup(): void {
  //   this.showAdminPopup = !this.showAdminPopup;

  //   this.showNavPopup = false;
  //   this.showSitePopup = false;
  //   this.showHelpPopup = false;
  //   this.showFavPopup = false;
  // }

  // public toggleFavPopup(): void {
  //   this.showFavPopup = !this.showFavPopup;

  //   this.showNavPopup = false;
  //   this.showSitePopup = false;
  //   this.showAdminPopup = false;
  //   this.showHelpPopup = false;
  // }

  _checkSecurity() {
    this._securityService.getLicense().subscribe((data: any) => {
      if (data.general.numberOfSites > 1) {
        this._isMultiSite = true;
      }

      if (this._isMultiSite == true) {
        this._checkCurrentUserSite();
      }
    });
  }

  _checkCurrentUserSite() {
    var loginInSites = JSON.parse(localStorage.getItem('loggedInAccountSites'));
    let siteId = loginInSites[0].siteId;

    if (siteId == SitesEnum.AllSites) {
      this._currentSiteName = 'All Sites';
    }
    else {
      this._sitesService.getSiteById(siteId).subscribe((data: any) => {
        this._currentSiteName = data.code;
        this.selectedSite = data;
      });
    }

    this._getUserSites();
  }

  _getUserSites() {
    this._accountsites.GetAccountsSitesDrop().subscribe((data: any) => {
      this._items = data.value ?? data.data.value;
      this.selectedAccount = this._items[0];

      this._userSites = [...this._items[0].sites];

      if (this._currentSiteName == 'All Sites') {
        this.selectedSite = this._userSites.find(x => x.id == SitesEnum.AllSites);
      }
    });
  }

  _switchSites(event: any) {
    var _selectedAccountsAndSitesArray: any[] = [];
    var _selectedAccountsAndSites: any = {
      siteId: this.selectedSite.id,
      accountId: this.selectedAccount.id
    };

    _selectedAccountsAndSitesArray.push(_selectedAccountsAndSites);
    this._loggedInAccountSites = JSON.stringify(_selectedAccountsAndSitesArray);

    this._securityService.clearCache();

    localStorage.setItem('loggedInAccountSites', this._loggedInAccountSites);

    window.location.reload();
  }

  toggleTheme(event?: Event) {
    if (event) {
      event.preventDefault();
    }
    this.changeTheme(!this._themeToggleChecked);
  }

  changeTheme(val?: boolean) {
    if (typeof val === 'boolean') {
      this._themeToggleChecked = val;
    }
    setTimeout(() => {
      if (this._themeToggleChecked == true) {
        this._themeManager.setTheme('dark');
        localStorage.setItem('enterprise-current-theme', 'dark');
      }
      else {
        this._themeManager.setTheme('enterprise');
        localStorage.setItem('enterprise-current-theme', 'enterprise');
      }
    }, 1);
  }

  logout() {
    if (localStorage.getItem('accessToken')) {
      this._securityService.logout().subscribe();
    }

    this._securityService.removeLocalCredentials();
    localStorage.removeItem('sessionEnded')
  }

  _navigateUrl(url: string) {
    this.router.navigate([url]);
  }

  _getPageDescription(page: string, category: string) {
    var pagedescription = '';

    if (!(category == 'Reports' && (!(page == 'reports' || page == 'forecast')))) {
      switch (page.toLowerCase()) {
        case 'stockitems':
          pagedescription = 'Stock Item';
          break;
        case 'categories':
          pagedescription = 'Category';
          break;
        case 'uom':
          pagedescription = 'U/M';
          break;
        case 'customers':
          pagedescription = 'Customer';
          break;
        case 'addresses':
          pagedescription = 'Address';
          break;
        case 'vendors':
          pagedescription = 'Vendor';
          break;
        case 'files':
          pagedescription = 'Pictures';
          break;
        case 'documents':
          pagedescription = "Documents";
          break;
        case 'videos':
          pagedescription = "Videos";
          break;
        case 'reports':
          pagedescription = 'Reports';
          break;
        case 'forecast':
          pagedescription = 'Forecast';
          break;
        case 'purchaseorders':
          pagedescription = 'Purchase Orders';
          break;
        case 'locations':
          pagedescription = 'Location';
          break;
        case 'locationtypes':
          pagedescription = 'Location Type';
          break;
        case 'salesorders':
          pagedescription = 'Sales Orders';
          break;
        case 'picklist':
          pagedescription = 'Picklist';
          break;
        case 'history':
          pagedescription = 'All History';
          break;
        case 'receive-history':
          pagedescription = 'Receive';
          break;
        case 'move-history':
          pagedescription = 'Move';
          break;
        case 'issue-history':
          pagedescription = 'Issue';
          break;
        case 'dispose-history':
          pagedescription = 'Dispose';
          break;
        case 'assetadjust-transactions-history':
          pagedescription = 'Adjust History';
          break;
        case 'dispose-authorization-history':
          pagedescription = 'Dispose Authorization';
          break;
        case 'adjust-authorization-history':
          pagedescription = 'Adjust Authorization';
          break;
        case 'return-history':
          pagedescription = 'Return';
          break;
        case 'pack-history':
          pagedescription = 'Pack';
          break;
        case 'ship-history':
          pagedescription = 'Ship';
          break;
        case 'adjustinventory-history':
          pagedescription = 'Adjust Inventory';
          break;
        case 'assemblehistory':
          pagedescription = 'Assemble History';
          break;
        case 'stockphysicalinventoryhistory':
          pagedescription = 'Physical Inventory';
          break;
        case 'sitetransferhistory':
        case 'site-transfer-history':
        case 'siteTransferHistory':
          pagedescription = 'Site Transfer History';
          break;
        case 'checkout-transactions':
          pagedescription = 'Check Out';
          break;
        case 'checkin-transactions':
          pagedescription = 'Check In';
          break;
        case 'checkout-transactions-history':
          pagedescription = 'Checkout History';
          break;
        case 'checkin-transactions-history':
          pagedescription = 'Checkin History';
          break;
        case 'receive':
          pagedescription = 'Receive';
          break;
        case 'issue':
          pagedescription = 'Issue';
          break;
        case 'move':
          pagedescription = 'Move';
          break;
        case 'dispose':
          pagedescription = 'Dispose';
          break;
        case 'stockdisposeauthorizationtransaction':
          pagedescription = 'Dispose Authorization';
          break;
        case 'stockadjustauthorizationtransaction':
          pagedescription = 'Adjust Authorization';
          break;
        case 'return':
          pagedescription = 'Return';
          break;
        case 'pack':
          pagedescription = 'Pack';
          break;
        case 'ship':
          pagedescription = 'Ship';
          break;
        case 'kitting':
          pagedescription = 'Kitting';
          break;
        case 'breakdown':
          pagedescription = 'Breakdown';
          break;
        case 'adjust-inventory':
          pagedescription = 'Adjust Inventory';
          break;
        case 'physical-inventory':
          pagedescription = 'Physical Inventory';
          break;
        case PageNames.StockCycleCount:
          pagedescription = 'Stock Cycle Count';
          break;
        case PageNames.StockCycleCountAdjust:
          pagedescription = 'Stock Cycle Count - Adjust';
          break;
        case PageNames.StockCycleCountHistory:
          pagedescription = 'Stock Cycle Count History';
          break;
        case PageNames.StockCycleCountAdjustHistory:
          pagedescription = 'Stock Cycle Count Adjust History';
          break;
        case 'stock-to-asset':
          pagedescription = 'Stock To Asset';
          break;
        case 'common':
          pagedescription = 'Common';
          break;
        case 'departments':
          pagedescription = 'Department';
          break;
        case 'facility':
          pagedescription = 'Facility';
          break;
        case 'building':
          pagedescription = 'Building';
          break;
        case 'floor':
          pagedescription = 'Floor';
          break;
        case 'disposeto':
          pagedescription = 'Dispose To';
          break;



        case 'assets':
          pagedescription = 'Asset';
          break;
        case 'asset-models':
          pagedescription = 'Model';
          break;
        case 'asset-types':
          pagedescription = 'Model Type';
          break;
        case 'conditions':
          pagedescription = 'Condition';
          break;
        case 'fundingsource':
          pagedescription = 'Funding Source';
          break;
        case 'statuses':
          pagedescription = 'Status';
          break;
        case 'asset-reservation':
          pagedescription = 'Asset Reservation';
          break;
        case 'assetcheckin-transaction':
          pagedescription = 'Check In';
          break;
        case 'assetcheckout-transactions':
          pagedescription = 'Check Out';
          break;
        case 'assetdispose-transactions':
          pagedescription = 'Asset Dispose';
          break;
        case 'assetreceive-transactions':
          pagedescription = 'Asset Receive';
          break;
        case 'assetmove-transactions':
          pagedescription = 'Asset Move';
          break;
        case 'asset-all-history':
          pagedescription = 'Asset All History';
          break;
        case 'assetmove-transactions-history':
          pagedescription = 'Asset Move';
          break;
        case 'assetcheckout-transactions-history':
          pagedescription = 'Check Out';
          break;
        case 'assetcheckin-transactions-history':
          pagedescription = 'Check In';
          break;
        case 'assetdispose-transactions-history':
          pagedescription = 'Asset Dispose';
          break;
        case 'assetreceive-transactions-history':
          pagedescription = 'Asset Receive';
          break;
        case 'asset-edit-log':
          pagedescription = 'Asset Edit Log';
          break;
        case 'barcode-label':
          pagedescription = "Barcode Labels";
          break;
        case 'barcode-catalog':
          pagedescription = "Barcode Catalogs";
          break;
        case 'pre-printed-label':
          pagedescription = "Pre-Printed Labels";
          break;
        case 'assetphysicalinventory-transactions':
          pagedescription = "Asset Physical Inventory";
          break;
        case 'assetphysicalinventory-history':
          pagedescription = "Asset Physical Inventory History";
          break;
        case 'assetdepreciation-transactions':
          pagedescription = "Depreciation";
          break;
        case 'stockdispose-authorization-history':
          pagedescription = "Dispose Authorization";
          break;
        case 'stockadjust-authorization-history':
          pagedescription = "Adjust Authorization";
          break;
        case 'assetdispose-authorization':
          pagedescription = 'Dispose Authorization';
          break;
        case 'assetadjust-authorization':
          pagedescription = 'Adjust Authorization';
          break;
        case 'assetadjust-transactions':
          pagedescription = 'Adjust';
          break;
        case 'assetdispose-authorization-history':
          pagedescription = 'Dispose Authorization';
          break;
        case 'assetadjust-authorization-history':
          pagedescription = 'Adjust Authorization';
          break;




        default:
          pagedescription = page;
          break;
      }
    }
    else {
      pagedescription = page;
    }

    return pagedescription;
  }

  _openForm(formName: string) {
    const isAssetModule = !!(this.router.url && this.router.url.includes('/asset'));
    var _dialogModel: any = {
      componentName: formName,
      model: {
        isAsset: isAssetModule,
        accessFrom: isAssetModule ? 'assets' : 'stocks'
      },
      pageState: PageState.ADD,
    };

    this._sharedService.openDialog.emit(_dialogModel);
    // this._layoutComponent.openDialogForm(formComponentMapOptions[formName], PageState.ADD);
  }

  _favorite(favObject: any) {
    const model: any = {};
    model.Page = favObject.page;
    model.Category = favObject.category;
    model.notPage = favObject.entityId != null;
    this._favoritesService.updateFavorites(model).subscribe(() => {
      this._getFavoritePages();
    });
  }

  _getFavoritePages() {
    this._favoritesService.getFavorites().subscribe((response: any) => {
      this._layoutManagerService.setFavorites(response);
    });
  }

  _navigate(pageName: string, pageCategory: string) {
    if (pageCategory == 'Reports' && pageName != 'reports' && pageName != 'forecast') {
      this._reportService.ReportIdByName(pageName).subscribe((reportid: any) => {
        this._layoutComponent.openDialogForm(ReportViewerPopupComponent, PageState.EDIT, { reportId: reportid });
      });
    }
    else {
      let url = '';

      switch (pageName.toLowerCase()) {
        case 'stockitems':
          url = 'stock/files/items';
          break;
        case 'categories':
          url = this.base == "stock" ? 'stock/files/categories' : 'asset/files/categories';
          break;
        case 'uom':
          url = 'stock/files/uom';
          break;
        case 'customers':
          url = 'stock/files/customers';
          break;
        case 'addresses':
          url = this.base == "stock" ? 'stock/files/address' : 'asset/files/address';
          break;
        case 'vendors':
          url = this.base == "stock" ? 'stock/files/vendors' : 'asset/files/vendors';
          break;
        case 'files':
          url = this.base == "stock" ? 'stock/pictures' : 'asset/pictures';
          break;
        case 'documents':
          url = this.base == "stock" ? 'stock/documents' : 'asset/documents';
          break;
        case 'videos':
          url = this.base == "stock" ? 'stock/videos' : 'asset/videos';
          break;
        case 'locations':
          url = this.base == "stock" ? 'stock/files/locations' : 'asset/files/locations';
          break;
        case 'locationtypes':
          url = this.base == "stock" ? 'stock/files/location-types' : 'asset/files/location-types';
          break;
        case 'facility':
          url = this.base == "stock" ? 'stock/files/facility' : 'asset/files/facility';
          break;
        case 'building':
          url = this.base == "stock" ? 'stock/files/building' : 'asset/files/building';
          break;
        case 'floor':
          url = this.base == "stock" ? 'stock/files/floor' : 'asset/files/floor';
          break;
        case 'reports':
          url = this.base == "stock" ? 'stock/report/reports' : 'asset/report/reports';
          break;
        case 'forecast':
          url = this.base == "stock" ? 'stock/report/forecast' : 'asset/report/forecast';
          break;
        case 'checkout-transactions':
          url = 'stock/transaction/checkout';
          break;
        case 'checkin-transactions':
          url = 'stock/transaction/checkin';
          break;
        case 'checkout-transactions-history':
          url = 'stock/history/checkout-history';
          break;
        case 'checkin-transactions-history':
          url = 'stock/history/checkin-history';
          break;
        case 'receive':
          url = 'stock/transaction/receive';
          break;
        case 'issue':
          url = 'stock/transaction/issue';
          break;
        case 'move':
          url = 'stock/transaction/move';
          break;
        case 'dispose':
          url = 'stock/transaction/dispose';
          break;
        case 'return':
          url = 'stock/transaction/return';
          break;
        case 'pack':
          url = 'stock/transaction/pack';
          break;
        case 'ship':
          url = 'stock/transaction/ship';
          break;
        case 'kitting':
          url = 'stock/transaction/kitting';
          break;
        case 'breakdown':
          url = 'stock/transaction/breakdown';
          break;
        case 'adjust-inventory':
          url = 'stock/transaction/adjust-inventory';
          break;
        case 'picklist':
          url = 'stock/picklist';
          break;
        case 'physical-inventory':
          url = 'stock/transaction/physical-inventory';
          break;
        case PageNames.StockCycleCount:
          url = 'stock/transaction/cycle-count';
          break;
        case PageNames.StockCycleCountAdjust:
          url = 'stock/transaction/cycle-count-adjust';
          break;
        case PageNames.StockCycleCountHistory:
          url = 'stock/history/cycle-count-history';
          break;
        case PageNames.StockCycleCountAdjustHistory:
          url = 'stock/history/cycle-count-adjust-history';
          break;
        case 'stock-to-asset':
          url = 'stock/stock-to-asset';
          break;
        case 'purchaseorders':
          url = 'stock/order/purchase-orders';
          break;
        case 'salesorders':
          url = 'stock/order/sales-orders';
          break;
        case 'history':
          url = 'stock/history/all';
          break;
        case 'receive-history':
          url = 'stock/history/receive-history';
          break;
        case 'move-history':
          url = 'stock/history/move-history';
          break;
        case 'issue-history':
          url = 'stock/history/issue-history';
          break;
        case 'dispose-history':
          url = 'stock/history/dispose-history';
          break;
        case 'dispose-authorization-history':
          url = 'stock/history/dispose-authorization-history';
          break;
        case 'adjust-authorization-history':
          url = 'stock/history/adjust-authorization-history';
          break;
        case 'return-history':
          url = 'stock/history/return-history';
          break;
        case 'pack-history':
          url = 'stock/history/pack-history';
          break;
        case 'ship-history':
          url = 'stock/history/ship-history';
          break;
        case 'adjustinventory-history':
          url = 'stock/history/adjustInventory-history';
          break;
        case 'assemblehistory':
          url = 'stock/history/assemble-history';
          break;
        case 'stockphysicalinventoryhistory':
          url = 'stock/history/physicalInventory-history';
          break;
        case 'sitetransferhistory':
        case 'site-transfer-history':
        case 'siteTransferHistory':
          url = 'stock/history/site-transfer-history';
          break;
        case 'common':
          url = 'common';
          break;
        case 'disposeto':
          url = this.base == "stock" ? 'stock/files/dispose-to' : 'asset/files/dispose-to';
          break;
        case 'departments':
          url = this.base == "stock" ? 'common/stock/department' : 'common/asset/department';
          break;
        case 'assets':
          url = 'asset/files/assets';
          break;
        case 'asset-models':
          url = 'asset/files/asset-model';
          break;
        case 'asset-types':
          url = 'asset/files/asset-type';
          break;
        case 'conditions':
          url = this.base == "stock" ? 'stock/files/conditions' : 'asset/files/conditions';
          break;
        case 'fundingsource':
          url = 'asset/files/fundingsource';
          break;
        case 'statuses':
          url = 'asset/files/status';
          break;
        case 'asset-reservation':
          url = 'asset/transactions/reservation';
          break;
        case 'assetcheckin-transaction':
          url = 'asset/transactions/checkin';
          break;
        case 'assetcheckout-transactions':
          url = 'asset/transactions/checkout';
          break;
        case 'assetdispose-transactions':
          url = 'asset/transactions/dispose';
          break;
        case 'assetreceive-transactions':
          url = 'asset/transactions/receive';
          break;
        case 'assetmove-transactions':
          url = 'asset/transactions/move';
          break;
        case 'asset-all-history':
          url = 'asset/history/all';
          break;
        case 'assetmove-transactions-history':
          url = 'asset/history/move-history';
          break;
        case 'assetcheckout-transactions-history':
          url = 'asset/history/checkout-history';
          break;
        case 'assetcheckin-transactions-history':
          url = 'asset/history/checkin-history';
          break;
        case 'assetdispose-transactions-history':
          url = 'asset/history/dispose-history';
          break;
        case 'assetreceive-transactions-history':
          url = 'asset/history/receive-history';
          break;
        case 'asset-edit-log':
          url = 'asset/history/edit-log';
          break;
        case 'barcode-label':
          url = this.base == "stock" ? 'stock/label/barcode-label' : 'asset/label/barcode-label';
          break;
        case 'barcode-catalog':
          url = this.base == "stock" ? 'stock/label/barcode-catalog' : 'asset/label/barcode-catalog';
          break;
        case 'pre-printed-label':
          url = this.base == "stock" ? 'stock/label/pre-printed-label' : 'asset/label/pre-printed-label';
          break;
        case 'assetphysicalinventory-transactions':
          url = 'asset/transactions/physicalinventory';
          break;
        case 'assetphysicalinventory-history':
          url = 'asset/history/assetphysicalinventory-history';
          break;
        case 'assetdepreciation-transactions':
          url = 'asset/transactions/depreciation';
          break;
        case 'stockdispose-authorization-history':
          url = 'stock/history/dispose-authorization-history';
          break;
        case 'stockadjust-authorization-history':
          url = 'stock/history/adjust-authorization-history';
          break;
        case 'stockadjustauthorizationtransaction':
          url = 'stock/transaction/adjust-authorization';
          break;
        case 'assetdispose-authorization':
          url = 'asset/transactions/dispose-authorization';
          break;
        case 'assetadjust-authorization':
          url = 'asset/transactions/adjust-authorization';
          break;
        case 'assetadjust-transactions':
          url = 'asset/transactions/adjust';
          break;
        case 'assetdispose-authorization-history':
          url = 'asset/history/dispose-authorization-history';
          break;
        case 'assetadjust-authorization-history':
          url = 'asset/history/adjust-authorization-history';
          break;
        case 'assetadjust-transactions-history':
          url = 'asset/history/adjust-history';
          break;

        default:
          break;
      }

      if (url != '') {
        this.router.navigate([url]);
      }
    }
  }

  _mainSwitch(event: string) {
    if (event == "ShoppingCart") {
      const accessToken = localStorage.getItem('accessToken');

      if (accessToken) {
        var tokenObj = JSON.parse(accessToken);

        const shoppingCartUrl = EnvironmentLoaderService.envConfig.shoppingCartUrl;

        //window.location.href = `${shoppingCartUrl}?accessToken=${tokenObj.token}`;
        window.open(`${shoppingCartUrl}?accessToken=${tokenObj.token}`, '_blank');
      }

      return;
    }

    this.router.navigate([`./${event}`]);
  }

  sitesEnabled() {
    return (this.canAccess('sites', 'create') || this.canAccess('sites', 'update')
      || this.canAccess('sites', 'delete') || this.canAccess('sites', 'read'));
  }

  canAccess(scope: string, operation: string) {
    return this._securityService.hasPermission(null, `${scope}-${operation}`);
  }
}
